import {
  BackgroundImage,
  Box,
  Card,
  Flex,
  Image,
  Space,
  Stack,
  Text,
} from "@mantine/core";
import { ReactNode } from "react";
import bgPayment from "assets/images/pngs/bgpayment.png";
import JavisLogo from "@/assets/images/pngs/iconJavis.png";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "@/utils/common";
import { beText } from "@/utils/beText";
import {
  formatMoney,
  getCurrencyScale,
} from "@/modules/transfer-money/transfer-international/currencyFormat";

interface DetailObject {
  key?: string;
  name?: string;
  display?: string;
  value?: string;
}

interface Props {
  transaction?: DetailObject[];
  transactionDetail?: any;
  /** Nội dung bổ sung render bên trong card, ngay dưới danh sách thông tin. */
  children?: ReactNode;
  /** Nhãn tiêu đề số tiền (mặc định t("AMOUNT") = "Số tiền"). */
  amountLabel?: string;
  /** Nhãn tiêu đề thẻ tài khoản (mặc định t("FROM_ACCOUNT") = "Tài Khoản"). */
  accountLabel?: string;
  /**
   * Căn lề nội dung thẻ tài khoản: "center" (mặc định) giữ layout cũ; "left" căn
   * trái, xếp số tài khoản và tên ngân hàng theo chiều dọc (khớp Figma CTQT).
   */
  accountAlign?: "center" | "left";
  /**
   * Khi bật, định dạng số tiền theo số chữ số thập phân của loại tiền tệ
   * (ISO 4217: USD/EUR=2, VND/JPY=0, KWD/BHD=3) — dùng cho luồng quốc tế.
   * Mặc định tắt để giữ định dạng vi-VN cho các loại giao dịch khác.
   */
  applyCurrencyScale?: boolean;
}

const DetailGeneric = ({
  transaction,
  transactionDetail,
  children,
  amountLabel,
  accountLabel,
  accountAlign = "center",
  applyCurrencyScale = false,
}: Props) => {
  const { t } = useTranslation();
  const amount = transactionDetail?.amount;
  const currency = transactionDetail?.currency || "VND";
  const alignLeft = accountAlign === "left";
  const getAmountDisplay = () => {
    if (applyCurrencyScale) {
      return formatMoney(
        Number(String(amount ?? "").replace(/,/g, "")),
        getCurrencyScale(currency)
      );
    }
    return amount ? formatCurrency(amount) : "0";
  };
  const amountDisplay = getAmountDisplay();

  return (
    <Card shadow="sm" radius="md" sx={{ padding: "20px" }}>
      <BackgroundImage
        src={bgPayment}
        style={{
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
        }}
      >
        <Stack p={20} spacing={8}>
          <Text fz={16} fw={500} c="#fff" ta="center">
            {amountLabel || t("AMOUNT")}
          </Text>
          <Text fz={30} fw={900} c="#fff" ta="center">
            {amountDisplay} {currency}
          </Text>
          {transactionDetail?.fromAccountNo && (
            <Box
              bg="#FFFFFF"
              mt={12}
              py={16}
              px={alignLeft ? 16 : 0}
              w="100%"
              style={{ borderRadius: "8px" }}
            >
              <Stack spacing={10} align={alignLeft ? "stretch" : "center"}>
                <Text fz={14} fw={700} color="black">
                  {accountLabel || t("FROM_ACCOUNT")}
                </Text>
                {/* Left-align (CTQT): account info xếp 3 dòng dọc -> logo canh
                    lên trên theo dòng đầu (tên KH) khớp design; center (2 dòng)
                    -> canh giữa như cũ. */}
                <Flex gap={12} align={alignLeft ? "flex-start" : "center"}>
                  <Image
                    src={transactionDetail?.fromLogoBank || JavisLogo}
                    alt="Bank logo"
                    width={32}
                    height={32}
                    style={{ flexShrink: 0 }}
                  />
                  <Stack spacing={alignLeft ? 2 : 0}>
                    <Text fz={14} fw={700} color="black">
                      {transactionDetail?.fromAccountFullName || ""}
                    </Text>
                    {alignLeft ? (
                      <>
                        <Text color="blue" fw={600} fz={14}>
                          {transactionDetail?.fromAccountNo || ""}
                        </Text>
                        {transactionDetail?.fromBankName && (
                          <Text fw={400} fz={13} color="#4D5562">
                            {transactionDetail.fromBankName}
                          </Text>
                        )}
                      </>
                    ) : (
                      <Flex gap={8}>
                        <Text color="blue" fw={600}>
                          {transactionDetail?.fromAccountNo || ""}
                        </Text>
                        {transactionDetail?.fromBankName && (
                          <>
                            <Text color="#E4E4E4">|</Text>
                            <Text fw={400} color="#4D5562">
                              {transactionDetail.fromBankName}
                            </Text>
                          </>
                        )}
                      </Flex>
                    )}
                  </Stack>
                </Flex>
              </Stack>
            </Box>
          )}
        </Stack>
      </BackgroundImage>
      <Space h={20} />
      {/* #53970: bỏ thụt lề 60px của khối key-value — card ngoài đã có padding 20px, thêm px=60 khiến
          các dòng thông tin thụt sâu hơn hẳn card "Số tiền" phía trên, lệch với các loại giao dịch
          khác (TransactionInfo/PaymentTransfer không thụt). px=0 để các dòng thẳng mép card. */}
      <Stack px={0} spacing={20}>
        {transaction && transaction.length > 0 ? (
          transaction.map((item, index) => (
            <Flex
              justify="space-between"
              key={`${item?.key ?? ""}-${item?.name ?? index}`}
            >
              <Text fz={14} c="#0E121B">
                {beText(item?.display || item?.name || item?.key)}
              </Text>
              <Text fz={14} fw={600}>
                {beText(item?.value)}
              </Text>
            </Flex>
          ))
        ) : (
          <Text fz="sm" c="dimmed" ta="center">
            -
          </Text>
        )}
      </Stack>
      {children && (
        <>
          <Space h={20} />
          {/* #53970: đồng bộ với khối key-value phía trên — bỏ thụt lề 60px. */}
          <Box px={0}>{children}</Box>
        </>
      )}
    </Card>
  );
};

export default DetailGeneric;
